import { Injectable } from '@angular/core';
import { SplitFactory } from '@splitsoftware/splitio-browserjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export const FEATURE_FLAG_DEFAULTS: { [identifier: string]: boolean } = {
  'release-quickbooks-sale-je': false,
  'release-quickbooks-check-payment': false,
  'release-quickbooks-stripe-posting': false,
  'release-quickbooks-ach-receipt': false,
  'release-auctioneer-console': false,
  'release-shot-clock-mode': false
};
export const RELEASE_AUCTIONEER_CONSOLE_FLAG = 'release-auctioneer-console';

// FME returns a treatment string, not a boolean. 'control' is what the SDK returns when it is
// not ready or does not know the flag, so it must never be read as "off" — it means "no answer",
// and the in-code default decides. See .agent/context/feature-flags.md.
export const ON_TREATMENT = 'on';
export const OFF_TREATMENT = 'off';

// Bounds how long a payment-adjacent call can wait on the SDK before the in-code default wins.
export const READY_TIMEOUT_SECONDS = 3;

// FME needs a targeting key for every evaluation, and these flags are targeted at a user or a
// tenant — never at a deploy stage. Signed-out visitors have no user, so they evaluate against
// this constant. It is deliberately not a stage name: each stage already has its own FME
// environment and client key, so the stage adds nothing to the key, while a real stage name
// here would make a rule written to target a user look like it had matched one.
export const ANONYMOUS_TARGET_KEY = 'anonymous';

export interface FeatureFlagConfig {
  fme_client_key?: string;
}

@Injectable()
export class FeatureFlagService {
  private factory: any = null;
  // The targeting key the cached client was built for. It changes when someone signs in or out.
  private clientKey: string = null;
  private clientPromise: Promise<any> = null;

  constructor(private authService: AuthService) {}

  enabled(identifier: string): Promise<boolean> {
    const fallback = FEATURE_FLAG_DEFAULTS[identifier] === true;
    let config: FeatureFlagConfig;
    try {
      config = this.config();
    } catch (error) {
      console.error('FeatureFlagService: failed to read environment config for ' + identifier
        + '; falling back to ' + fallback, error);
      return Promise.resolve(fallback);
    }
    if (!config || !config.fme_client_key) {
      return Promise.resolve(fallback);
    }

    let targetKey: string;
    try {
      targetKey = this.targetKey();
    } catch (error) {
      // A flag evaluation must not be the thing that breaks because auth state is half-built.
      console.error('FeatureFlagService: failed to read the targeting key for ' + identifier
        + '; evaluating as ' + ANONYMOUS_TARGET_KEY, error);
      targetKey = ANONYMOUS_TARGET_KEY;
    }

    const pending = this.readyClient(config, targetKey);
    return pending.then((client) => {
      return this.treatmentToBool(client.getTreatment(identifier), identifier, targetKey, fallback);
    }).catch((error) => {
      // Tear the factory down so the next call rebuilds it. A browser session outlives a
      // transient SDK failure, and without this every flag would stay pinned to its default
      // for the rest of the tab's life. Only if this evaluation is still the current one:
      // a superseded request rejecting late (a signed-out readiness timeout landing after
      // sign-in installed a healthy client) would otherwise destroy the factory out from
      // under the newer key — factory.destroy() tears down every client it handed out.
      this.discardIfCurrent(pending);
      console.error('FeatureFlagService: FME evaluation failed for ' + identifier
        + ' as ' + targetKey + '; falling back to ' + fallback, error);
      return fallback;
    });
  }

  // The FME targeting key: the signed-in user's account id when there is one. Anyone not signed
  // in — the public catalogue, the sign-in page itself — has no user id to target, and gets the
  // anonymous key rather than borrowing someone else's treatments.
  protected targetKey(): string {
    const user = this.authService && this.authService.activeUser;
    const accountId = user && user.accountId;
    if (accountId) {
      return String(accountId);
    }
    // activeUser is populated by an awaited round trip after Cognito sign-in, and that round
    // trip swallows its own failures. So "Cognito says signed in but activeUser is empty" is
    // a real state, and evaluating it as anonymous is a per-user rule that silently did not
    // apply — indistinguishable from a deliberate off unless it is said out loud here.
    if (this.authService && this.authService.amplifySignedIn) {
      console.error('FeatureFlagService: signed in but activeUser is not loaded; evaluating as '
        + ANONYMOUS_TARGET_KEY + ' — this answer is not user-targeted');
    }
    return ANONYMOUS_TARGET_KEY;
  }

  // Environment config lives in src/environments/environment.*.ts, which Angular swaps per
  // build configuration. Do not read /assets/env.json. See .agent/context/feature-flags.md.
  protected config(): FeatureFlagConfig {
    return {
      fme_client_key: environment.fme_client_key
    };
  }

  // Seam for tests. One factory per app; targeting keys are supplied per client below.
  protected createFactory(config: FeatureFlagConfig): any {
    return SplitFactory({
      core: {
        authorizationKey: config.fme_client_key,
        // The factory's own key. Evaluations always ask for a client by key, so this is only
        // the client a signed-out visitor ends up on.
        key: ANONYMOUS_TARGET_KEY
      },
      startup: {
        readyTimeout: READY_TIMEOUT_SECONDS
      }
    } as any);
  }

  private readyClient(config: FeatureFlagConfig, targetKey: string): Promise<any> {
    if (this.clientPromise && this.clientKey === targetKey) {
      return this.clientPromise;
    }
    // Signing in or out changes the key mid-session. The SDK caches one client per key on the
    // factory, so the previous client is dropped rather than destroyed. Destroying it is only
    // unsafe for the factory's own key (that call tears down the factory), but destroying a
    // shared client an in-flight evaluation is still awaiting would turn its answer into a
    // 'control' fallback. The cost is that a departed key's client keeps polling until the
    // factory is torn down; a tab sees at most a couple of keys. See feature-flags.md.
    this.clientKey = targetKey;
    const pending: Promise<any> = this.clientPromise = new Promise((resolve, reject) => {
      let client: any;
      try {
        if (!this.factory) {
          this.factory = this.createFactory(config);
        }
        client = this.factory.client(targetKey);
      } catch (error) {
        reject(error);
        return;
      }
      // ready() rejects on readyTimeout, which is the case that matters here: an SDK that
      // never reaches the backend must not hold a payment-adjacent decision open.
      client.ready().then(() => resolve(client), reject);
    }).catch((error) => {
      // Same guard as in enabled(): only the current evaluation may tear the factory down.
      this.discardIfCurrent(pending);
      throw error;
    });
    return pending;
  }

  private discardIfCurrent(pending: Promise<any>): void {
    if (this.clientPromise === pending) {
      this.discardFactory();
    }
  }

  private discardFactory(): void {
    const factory = this.factory;
    this.factory = null;
    this.clientKey = null;
    this.clientPromise = null;
    if (factory && typeof factory.destroy === 'function') {
      try {
        // Stops the polling and telemetry of every client this factory handed out. It returns a
        // promise; a rejected teardown must not surface as the flag's answer.
        const destroyed = factory.destroy();
        if (destroyed && typeof destroyed.catch === 'function') {
          // Kept out of the flag's answer, but not discarded: a teardown that keeps failing
          // leaves sync timers running and is otherwise invisible.
          destroyed.catch((error) => {
            console.error('FeatureFlagService: FME factory teardown failed', error);
          });
        }
      } catch (error) {
        console.error('FeatureFlagService: failed to destroy the FME factory', error);
      }
    }
  }

  private treatmentToBool(
    treatment: any, identifier: string, targetKey: string, fallback: boolean
  ): boolean {
    if (treatment === ON_TREATMENT) {
      return true;
    }
    if (treatment === OFF_TREATMENT) {
      return false;
    }
    // The targeting key belongs in this line: since the key now varies per evaluation, it is
    // the one field that tells "the rule did not match this user" from "the SDK had no answer".
    console.error('FeatureFlagService: FME returned treatment "' + treatment + '" for '
      + identifier + ' as ' + targetKey + '; falling back to ' + fallback);
    return fallback;
  }
}
